<template>
  <!-- Компонент вопрос -->
  <QuizQuestion
    :question="question"
    @emotion-slide-changed="onEmotionSlideChanged"
    ref="quizQuestion"
  />
  <!-- Компонент вопрос -->

  <div class="quiz-options quiz__single-choice">
    <div
      v-for="(option, optionIndex) in question.options"
      :key="optionIndex"
      class="quiz__option"
      :class="{
          'selected': isOptionSelected(option),
          'disabled': !canSelectOption(option)
        }"
      @click="selectOption(option, currentEmotionIndex)"
    >
      <span>{{ option.text || option }}</span>
      <div class="quiz__option-icon">
        <svg class="icon-correct">
          <use xlink:href="/static/img/sprites/sprite.svg#correct"></use>
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
import QuizQuestion from "@/components/QuizQuestion/QuizQuestion.vue";

export default {
  name: 'EmotionRecognitionQuestion',
  components: {
    QuizQuestion
  },

  props: {
    question: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      currentEmotionIndex: 0,
      selectedOptions: {},
      emotions: this.question.emotions || [],
      notifiedComplete: false // флаг для отслеживания оповещения о готовности
    };
  },

  computed: {
    availableOptions() {
      return this.question.options || [];
    },

    isComplete() {
      // Вопрос завершен, если для каждой эмоции выбран вариант
      return this.emotions.every((_, index) => this.selectedOptions[index] !== undefined);
    }
  },

  methods: {
    // Обработчик смены слайда от QuizQuestion
    onEmotionSlideChanged(index) {
      this.currentEmotionIndex = index;
    },

    setCurrentEmotion(index) {
      if (index >= 0 && index < this.emotions.length) {
        this.currentEmotionIndex = index;
        // Программно переключаем слайд в QuizQuestion
        this.$nextTick(() => {
          if (this.$refs.quizQuestion && this.$refs.quizQuestion.setSlide) {
            this.$refs.quizQuestion.setSlide(index);
          }
        });
      }
    },

    selectOption(option, emotionIndex) {
      if (this.canSelectOption(option)) {
        // Сохраняем выбранный вариант для текущей эмоции
        this.selectedOptions[emotionIndex] = option;

        // Эмитируем событие для обновления ответов в родительском компоненте
        this.$emit('answers-updated', this.question.id, this.selectedOptions);

        // Автоматически переходим к следующей эмоции, если не последняя
        if (emotionIndex < this.emotions.length - 1) {
          this.setCurrentEmotion(emotionIndex + 1);
        }

        // Проверяем завершенность выбора и уведомляем родительский компонент
        this.checkCompleteness();
      }
    },

    // Проверяет завершенность выбора и уведомляет родительский компонент
    checkCompleteness() {
      if (this.isComplete && !this.notifiedComplete) {
        this.notifiedComplete = true;
        // Эмитируем событие для родительского компонента
        this.$emit('answers-updated', this.question.id, this.selectedOptions);
      }
    },

    isOptionSelected(option) {
      return Object.values(this.selectedOptions).includes(option);
    },

    canSelectOption(option) {
      // Если вопрос завершен, все опции должны быть заблокированы
      if (this.isComplete) {
        return false;
      }

      // Опция недоступна, если она уже выбрана для другой эмоции
      // и не является текущей выбранной опцией для текущей эмоции
      const isSelectedForOtherEmotion = Object.entries(this.selectedOptions).some(([emotionIndex, selectedOption]) => {
        return parseInt(emotionIndex) !== this.currentEmotionIndex && selectedOption === option;
      });

      return !isSelectedForOtherEmotion;
    },

    submitAnswer() {
      if (this.isComplete) {
        const answers = [];

        // Формируем массив ответов в формате, который ожидает бэкенд
        for (let i = 0; i < this.emotions.length; i++) {
          answers.push({
            emotionIndex: i,
            selectedOption: this.selectedOptions[i],
            correctOption: this.emotions[i].correct_emotion
          });
        }

        // Отправляем ответ вместе с id вопроса
        this.$emit('answer-selected', this.question.id, {
          type: 'emotion_recognition',
          answers: answers,
          isCorrect: this.checkAllCorrect()
        });
      }
    },

    // Добавляем метод для внешнего вызова из родительского компонента
    // Этот метод может быть вызван при нажатии на кнопку "Далее" в родительском компоненте
    checkAndSubmitAnswer() {
      this.submitAnswer();
      return this.isComplete;
    },

    checkAllCorrect() {
      // Проверяем, все ли эмоции определены правильно
      return this.emotions.every((emotion, index) =>
        this.selectedOptions[index] === emotion.correct_emotion
      );
    },

    // Метод для отмены последнего выбора эмоции
    undoLastSelection() {
      // Находим последнюю выбранную эмоцию
      const selectedIndices = Object.keys(this.selectedOptions)
        .map(Number)
        .sort((a, b) => b - a); // Сортируем по убыванию

      if (selectedIndices.length > 0) {
        const lastSelectedIndex = selectedIndices[0];
        
        // Удаляем последний выбор
        delete this.selectedOptions[lastSelectedIndex];
        
        // Сбрасываем флаг уведомления о завершении
        this.notifiedComplete = false;
        
        // Определяем новый индекс слайдера
        let newCurrentIndex;
        
        // Проверяем, была ли это 4-я эмоция (последняя по порядку)
        if (lastSelectedIndex === this.emotions.length - 1) {
          // Если отменяем 4-ю эмоцию, остаемся на текущем слайде
          newCurrentIndex = this.currentEmotionIndex;
        } else if (selectedIndices.length > 1) {
          // Если есть другие выбранные эмоции, переключаемся на предыдущий слайд
          newCurrentIndex = Math.max(0, this.currentEmotionIndex - 1);
        } else {
          // Если это был последний выбор, переключаемся на первую эмоцию
          newCurrentIndex = 0;
        }
        
        this.setCurrentEmotion(newCurrentIndex);
        
        // Обновляем ответы в родительском компоненте
        this.$emit('answers-updated', this.question.id, this.selectedOptions);
      }
    }
  },

      mounted() {
  },

  watch: {
    // Отслеживаем смену вопроса для сброса состояния
    'question.id': {
      handler() {
        this.currentEmotionIndex = 0;
        this.selectedOptions = {};
        this.notifiedComplete = false;
        this.emotions = this.question.emotions || [];
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss">
.quiz-question {
  .quiz-question__slider {
    width: 100%;

    .swiper-slide {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 168px;
      width: 295px;
      border-radius: 16px;
      border: 1px solid var(--light-gray);
      background: var(--white);
      aspect-ratio: 3 / 2;

      @media (min-width: $sm) {
        width: 340px;
      }

      @media (min-width: $md) {
        height: 230px;
      }

      & img {
        max-height: 100px;

        @media (min-width: $lg) {
          max-height: 160px;
        }
      }
    }

    .swiper-pagination {
      .swiper-pagination-bullet {
        height: 8px;
        width: 8px;
        background-color: #B7B7B7;
        opacity: 1;

        &.swiper-pagination-bullet-active {
          width: 40px;
          height: 8px;
          border-radius: 18px;
        }
      }
    }
  }
}

</style>
